import { useState, useEffect } from 'react';
import type React from 'react';
import { Controller } from 'react-hook-form';
import type { Control, FieldErrors, FieldValues } from 'react-hook-form';


import { Stack } from 'ui/stack';

import Input from 'components/partials/input/input';
import Select from 'components/partials/select/select';

import { BASIC_INFO_FORM_FIELDS } from 'containers/admin/clients/client/forms/basic-info-form/basic-info-form-fileds.constants';
import type { BasicInfoFormField } from 'containers/admin/clients/client/forms/basic-info-form/basic-info-form-fileds.constants';

import { useReadOnly } from 'hooks/read-only';

const SUPER_ADMIN_ONLY_FIELDS = new Set(['identityProviderId']);

interface BasicInfoFormProps {
  isFormDisabled?: boolean;
  control: Control<FieldValues>;
  errors: FieldErrors;
  clearErrors: (name?: string) => void;
}

const BasicInfoForm = ({
  isFormDisabled,
  control,
  errors,
  clearErrors,
}: BasicInfoFormProps): React.JSX.Element => {
  const readOnly = useReadOnly();

  const [basicInformationFields, setBasicInformationFields] = useState<
    BasicInfoFormField[]
  >(BASIC_INFO_FORM_FIELDS);

  const filterBasicFields = (
    key: BasicInfoFormField['mode']
  ): BasicInfoFormField[] =>
    BASIC_INFO_FORM_FIELDS.filter(
      (field) => field.mode === key || field.mode === 'CREATE_AND_EDIT_MODE'
    ).map((field) => ({
      ...field,
      disabled: SUPER_ADMIN_ONLY_FIELDS.has(field.name) && readOnly,
    }));

  useEffect(() => {
    const filterKey: BasicInfoFormField['mode'] = isFormDisabled
      ? 'EDIT_MODE'
      : 'CREATE_MODE';

    setBasicInformationFields(filterBasicFields(filterKey));
  }, [readOnly]);

  return (
    <Stack sx={{ width: '50%', mr: 8 }} data-testid="basic-info-form">
      {basicInformationFields.map(
        ({ label, name, key, rules, isSelect, componentProps, disabled }) => (
          <Controller
            key={key}
            name={name}
            control={control}
            rules={rules}
            render={({ field }) =>
              isSelect ? (
                <Select
                  fieldError={errors[name] as never}
                  label={label}
                  clearInputErr={() => clearErrors(name)}
                  labelFor={name}
                  errors={errors as never}
                  disabled={isFormDisabled}
                  labelInTop
                  disabledBgColor={
                    isFormDisabled ? 'rgb(242 242 242)' : undefined
                  }
                  required={!isFormDisabled}
                  {...field}
                  value={field.value as string | undefined}
                  {...(componentProps as Record<string, unknown>)}
                />
              ) : (
                <Input
                  errorsNames={errors[name] as never}
                  label={label}
                  labelInTop
                  labelFor={name}
                  disabled={isFormDisabled || disabled}
                  disabledClassName={'bg-gray-100'}
                  required={!isFormDisabled}
                  {...field}
                  value={(field.value as string | undefined) ?? ''}
                  {...(componentProps as Record<string, unknown>)}
                />
              )
            }
          />
        )
      )}
    </Stack>
  );
};

export default BasicInfoForm;
